import * as _ from 'lodash';
import * as moment from 'moment';
import * as React from 'react';
import * as ReactTooltip from 'react-tooltip';

import * as Models from '../Models';
import { selectLink } from './links';
import { CHEVRON_DOWN, CHEVRON_UP, INFO } from '../Constants';
import { OutotecIcons } from '../Components/General/Icons';
import { Row } from 'react-table'

export const mapAttributeListToSparePartList: (
    rows: Models.AttributeRow[],
) => Models.SparePart[] = (rows) => {
    return rows.map((row) => {
        return mapAttributeRowToSparePart(row);
    });
}

export const mapAttributeRowToSparePart: (
    row: Models.AttributeRow,
) => Models.SparePart = (row) => {
    return row.items ? {
        itemNumber: row.items.length > 0 && row.items[0].description ? row.items[0].description : '',
        description: row.items.length > 1 && row.items[1].description ? row.items[1].description : '',
        technicalDescription: row.items.length > 2 && row.items[2].description ? row.items[2].description : '',
        isSparePart: row.items.length > 3 && row.items[3].description ? row.items[3].description : ''
    } : getEmptySparePart()
}

const customFilterMethod = (rows, columnId, filterValue) => {
    return rows.filter(row => {
        const value = row.values[columnId];
        return value && value.toLowerCase().includes(filterValue.toLowerCase());
    });
};

const sortByDescription = (rowA, rowB, columnId) => {
    const valueA = rowA.values[columnId] || ''; // Default to empty string if null or undefined
    const valueB = rowB.values[columnId] || ''; // Default to empty string if null or undefined

    const lowerA = valueA.toString().toLowerCase();
    const lowerB = valueB.toString().toLowerCase();

    if (lowerA < lowerB) return -1;  // valueA should come first
    if (lowerA > lowerB) return 1;   // valueB should come first
    return 0;  // equal, no change
};

export const getDataGridContent: (
    headerNames: string[],
    data: Models.ItemsApiSparePart[] | Models.SparePart[],
    changeRequests?: Models.ChangeRequest[],
) => Models.DataGridContent = (headerNames, data, changeRequests) => {
    const result: Models.DataGridContent = {
        columns: [],
        data: []
    }

    const headerMap: { [key: string]: string } = headerNames.reduce((previous, current) => {
        return { ...previous, [current.replace(/\s/g, '').toLowerCase()]: current };
    }, {});

    const sparePart: Models.SparePart = getEmptySparePart();

    result.columns = Object.keys(sparePart).map((key) => {
        const formatter: (value: any) => string = typeof (sparePart[key]) === 'boolean' ? (
            (value) => value as boolean === true ? 'True' : 'False'
        ) : (
            (value) => value
        )

        return {
            Header: headerMap[key.toLowerCase()] || '',
            show: headerMap[key.toLowerCase()] ? true : false,
            accessor: key,
            Cell: getCellComponent(formatter, changeRequests),
            Filter: ({ column }) => (
                <input
                    type='text'
                    value={column.filterValue || ''}
                    onChange={e => column.setFilter(e.target.value)}
                    placeholder="Filter..."
                    className='form-control'
                    style={{ width: '100%' }}
                />
            ),
            filter: customFilterMethod,
            sortType: sortByDescription,

        }
    });

    result.data = data;

    return result;
};

export const getCellComponent: (
    formatter: (value: any) => string,
    changeRequests?: Models.ChangeRequest[],
) => (props: any) => JSX.Element = (formatter, changeRequests) => {
    return (props: any) => {
        let toolTip: JSX.Element = null;

        return (
            <span
                className={`ItemGroup__cell ${toolTip ? 'ItemGroup__cell--hasPendingChange' : ''}`}
                title={formatter(props.value)}>
                <span className="ItemGroup__fieldChangedText">{formatter(props.value)}</span>
            </span>
        );
    }
};

export const getEmptySparePart: (
) => Models.SparePart = () => {
    return {
        itemNumber: '',
        description: '',
        technicalDescription: '',
        isSparePart: undefined
    };
};

export const withExpander: (
    removedSpareparts: Models.SparePartChange[],
    content: Models.DataGridContent,
) => Models.DataGridContent = (removedSpareparts, content) => {
    const expander: any = {
        expander: true,
        Expander: ({ isExpanded, ...rest }) => {
            const isRemoved: boolean = _.findIndex(removedSpareparts, (s: Models.SparePartChange) =>
                s.itemNumber === rest.original.itemNumber) > -1;
            return !isRemoved ? <span className="btn btn-primary btn-sm">Edit</span> : <span />;
        },
        width: 50,
    };

    return { ...content, columns: content.columns.concat(expander) };
};

export const withAdder: (
    content: Models.DataGridContent,
    addedSpareparts: Models.SparePartChange[],
    adderFunction: (data: Models.SparePartChange) => void,
) => Models.DataGridContent = (content, addedSpareparts, adderFunction) => {
    const adder: any = {
        Header: '',
        accessor: null,
        sortable: false,
        filterable: false,
        minWidth: 75,
        Cell: (props: any) => {
            const isAdded: boolean = _.findIndex(addedSpareparts, (s: Models.SparePartChange) =>
                s.itemNumber === props.original.itemNumber) > -1;
            const title: string = isAdded ? 'Undo' : 'Add';
            return (
                <span className="btn btn-primary btn-sm" title={title} onClick={(e) => adderFunction(props.original)}>
                    {title}
                </span>
            );
        }
    };

    return { ...content, columns: content.columns.concat(adder) };
};

export const withRemover: (
    content: Models.DataGridContent,
    removedSpareparts: Models.SparePartChange[],
    actionFunction: (data: Models.SparePartChange) => void,
) => Models.DataGridContent = (content, removedSpareparts, actionFunction) => {
    const remover: any = {
        Header: '',
        accessor: null,
        sortable: false,
        filterable: false,
        minWidth: 90,
        maxWidth: 90,
        Cell: (props: any) => {
            const isRemoved: boolean = _.findIndex(removedSpareparts, (s: Models.SparePartChange) =>
                s.itemNumber === props.original.itemNumber) > -1;
            const title: string = isRemoved ? 'Undo' : 'Remove';
            return (
                <span className="btn btn-danger btn-sm" title={title} onClick={(e) => actionFunction(props.original)}>
                    {title}
                </span>
            )
        },
    };

    return { ...content, columns: content.columns.concat(remover) };
};
